import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Close mobile menu on route change
    setMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/research', label: 'Research' },
    { path: '/technology', label: 'Technology' },
    { path: '/team', label: 'Team' },
    { path: '/partnerships', label: 'Partnerships' },
    { path: '/developers', label: 'Developers' },
    { path: '/news', label: 'News' },
    { path: '/careers', label: 'Careers' }
  ];

  return (
    <>
      <style>{`
        .navbar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: var(--z-sticky);
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(20px) saturate(180%);
          border-bottom: 1px solid var(--gray-200);
          transition: all var(--transition-base);
        }

        [data-theme="dark"] .navbar {
          background: rgba(24, 24, 27, 0.9);
        }

        .navbar.scrolled {
          background: rgba(255, 255, 255, 0.95);
          box-shadow: var(--shadow-sm);
          border-bottom-color: var(--gray-300);
        }

        [data-theme="dark"] .navbar.scrolled {
          background: rgba(24, 24, 27, 0.95);
        }

        .nav-container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          height: 80px;
        }

        .nav-left {
          display: flex;
          align-items: center;
          gap: 3rem;
        }

        .logo {
          font-size: 1.75rem;
          font-weight: 800;
          color: var(--black);
          text-decoration: none;
          letter-spacing: -0.05em;
          transition: all var(--transition-fast);
          text-transform: lowercase;
        }

        .logo:hover {
          transform: scale(1.02);
        }

        .nav-links {
          display: flex;
          gap: 2rem;
          list-style: none;
        }

        .nav-link {
          color: var(--gray-600);
          text-decoration: none;
          font-weight: 500;
          font-size: 0.95rem;
          transition: all var(--transition-fast);
          position: relative;
          padding: 0.5rem 0;
        }

        .nav-link::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 2px;
          background: var(--blue-600);
          transition: width var(--transition-base);
        }

        .nav-link:hover,
        .nav-link.active {
          color: var(--gray-900);
        }

        .nav-link:hover::after,
        .nav-link.active::after {
          width: 100%;
        }

        .nav-right {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .theme-toggle {
          width: 44px;
          height: 44px;
          border-radius: var(--radius);
          background: var(--gray-100);
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all var(--transition-fast);
          color: var(--gray-700);
        }

        .theme-toggle:hover {
          background: var(--gray-200);
          transform: scale(1.05);
        }

        .theme-toggle svg {
          width: 20px;
          height: 20px;
          transition: all var(--transition-base);
        }

        .theme-toggle:hover svg {
          transform: rotate(180deg);
        }

        .nav-cta {
          background: var(--black);
          color: var(--white);
          padding: 0.75rem 1.5rem;
          border-radius: var(--radius);
          text-decoration: none;
          font-weight: 600;
          font-size: 0.95rem;
          transition: all var(--transition-fast);
          position: relative;
          overflow: hidden;
          z-index: 1;
        }

        .nav-cta::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: var(--blue-600);
          transition: left var(--transition-base);
          z-index: -1;
        }

        .nav-cta:hover::before {
          left: 0;
        }

        .nav-cta:hover {
          transform: translateY(-1px);
          box-shadow: var(--shadow-md);
        }

        .mobile-menu-toggle {
          display: none;
          width: 44px;
          height: 44px;
          background: transparent;
          border: none;
          cursor: pointer;
          position: relative;
        }

        .hamburger {
          width: 24px;
          height: 2px;
          background: var(--gray-700);
          position: relative;
          transition: all var(--transition-base);
        }

        .hamburger::before,
        .hamburger::after {
          content: '';
          position: absolute;
          width: 24px;
          height: 2px;
          background: var(--gray-700);
          transition: all var(--transition-base);
        }

        .hamburger::before {
          top: -8px;
        }

        .hamburger::after {
          bottom: -8px;
        }

        .mobile-menu-toggle.open .hamburger {
          background: transparent;
        }

        .mobile-menu-toggle.open .hamburger::before {
          top: 0;
          transform: rotate(45deg);
        }

        .mobile-menu-toggle.open .hamburger::after {
          bottom: 0;
          transform: rotate(-45deg);
        }

        .mobile-menu {
          position: fixed;
          top: 80px;
          left: 0;
          right: 0;
          bottom: 0;
          background: var(--white);
          transform: translateX(100%);
          transition: transform var(--transition-base);
          z-index: var(--z-fixed);
          padding: 2rem;
          overflow-y: auto;
        }

        [data-theme="dark"] .mobile-menu {
          background: var(--gray-900);
        }

        .mobile-menu.open {
          transform: translateX(0);
        }

        .mobile-nav-links {
          list-style: none;
          padding: 0;
        }

        .mobile-nav-links li {
          margin-bottom: 1rem;
        }

        .mobile-nav-link {
          display: block;
          padding: 1rem;
          color: var(--gray-700);
          text-decoration: none;
          font-weight: 500;
          font-size: 1.125rem;
          border-radius: var(--radius);
          transition: all var(--transition-fast);
        }

        .mobile-nav-link:hover,
        .mobile-nav-link.active {
          background: var(--gray-100);
          color: var(--gray-900);
        }

        .mobile-menu-footer {
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 1px solid var(--gray-200);
        }

        @media (max-width: 1024px) {
          .nav-links {
            display: none;
          }

          .nav-cta.desktop {
            display: none;
          }

          .mobile-menu-toggle {
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }

        @media (max-width: 768px) {
          .nav-container {
            padding: 0 1rem;
          }
        }
      `}</style>

      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <div className="nav-left">
            <Link to="/" className="logo">angalifu</Link>
            <ul className="nav-links">
              {navLinks.slice(1, 6).map(link => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="nav-right">
            <button 
              className="theme-toggle" 
              onClick={toggleTheme}
              aria-label="Toggle theme"
            >
              {theme === 'light' ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="5" />
                  <line x1="12" y1="1" x2="12" y2="3" />
                  <line x1="12" y1="21" x2="12" y2="23" />
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                  <line x1="1" y1="12" x2="3" y2="12" />
                  <line x1="21" y1="12" x2="23" y2="12" />
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                </svg>
              )}
            </button>
            <Link to="/contact" className="nav-cta desktop">Get Started</Link>
            <button 
              className={`mobile-menu-toggle ${mobileMenuOpen ? 'open' : ''}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle mobile menu"
            >
              <span className="hamburger"></span>
            </button>
          </div>
        </div>
      </nav>

      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <ul className="mobile-nav-links">
          {navLinks.map(link => (
            <li key={link.path}>
              <Link 
                to={link.path} 
                className={`mobile-nav-link ${location.pathname === link.path ? 'active' : ''}`}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
        <div className="mobile-menu-footer">
          <Link to="/contact" className="nav-cta" style={{ display: 'block', textAlign: 'center' }}>
            Get Started
          </Link>
        </div>
      </div>
    </>
  );
};

export default Navigation;
